import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { BookOpen, Mic, Eye, X, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { trackJournalEntrySaved } from '../lib/analytics';

interface ContextJournalProps {
  checkinId?: string;
  relationshipId: string;
  onSave?: (note: string, voiceNote?: string) => void;
  tier: 'free' | 'basic' | 'premium' | 'pro';
}

export function ContextJournal({ checkinId, relationshipId, onSave, tier }: ContextJournalProps) {
  const [note, setNote] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [voiceNote, setVoiceNote] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [journalHistory, setJournalHistory] = useState<any[]>([]);

  const isPremium = tier === 'basic' || tier === 'premium' || tier === 'pro';
  const isPro = tier === 'pro';

  useEffect(() => {
    loadJournalHistory();
  }, [relationshipId]);

  const loadJournalHistory = () => {
    const stored = localStorage.getItem('tension_journal_entries');
    if (stored) {
      const entries = JSON.parse(stored).filter(
        (e: any) => e.relationshipId === relationshipId
      );
      setJournalHistory(entries);
    }
  };

  const handleSave = () => {
    if (!note.trim() && !voiceNote) {
      toast.error('Please add a note before saving');
      return;
    }

    const entry = {
      id: Date.now().toString(),
      relationshipId,
      checkinId,
      note: note.trim(),
      voiceNote,
      timestamp: new Date().toISOString(),
    };

    // Save to localStorage
    const stored = localStorage.getItem('tension_journal_entries') || '[]';
    const entries = JSON.parse(stored);
    entries.push(entry);
    localStorage.setItem('tension_journal_entries', JSON.stringify(entries));
    trackJournalEntrySaved({
      totalEntriesAfterSave: entries.length,
      hasVoiceNote: Boolean(voiceNote),
    });

    // Also attach to the check-in if provided
    if (checkinId) {
      const checkins = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
      const updatedCheckins = checkins.map((c: any) => 
        c.timestamp === checkinId ? { ...c, journalNote: note.trim() } : c
      );
      localStorage.setItem('tension_checkins', JSON.stringify(updatedCheckins));
    }

    toast.success('Journal entry saved');
    setNote('');
    setVoiceNote(null);
    loadJournalHistory();

    if (onSave) {
      onSave(note.trim(), voiceNote || undefined);
    }
  };

  const handleVoiceRecord = () => {
    if (!isPro) {
      toast.error('Voice notes are a Pro feature');
      return;
    }

    // In production, this would use Web Audio API
    if (isRecording) {
      setIsRecording(false);
      setVoiceNote('voice_note_' + Date.now());
      toast.success('Voice note recorded (demo)');
    } else {
      setIsRecording(true);
      toast.success('Recording started (demo)');
    }
  };

  const detectPatterns = () => {
    if (!isPremium || journalHistory.length < 3) return null;

    // Detect common phrases
    const allNotes = journalHistory.map(e => e.note.toLowerCase());
    const commonPhrases = [
      "didn't respond",
      "cancelled",
      "ignored",
      "double text",
      "anxious",
      "worried",
      "distance",
    ];

    const detected = commonPhrases
      .map(phrase => ({
        phrase,
        count: allNotes.filter(note => note.includes(phrase)).length,
      }))
      .filter(p => p.count >= 2)
      .sort((a, b) => b.count - a.count);

    return detected.length > 0 ? detected[0] : null;
  };

  const pattern = detectPatterns();
  const maxNoteLength = isPro ? 2000 : 500;
  const guidedPrompts = [
    'What did I feel before and after we interacted?',
    'What did they do that changed the emotional temperature?',
    'What do I want them to understand without pressure?',
    'What boundary would protect my peace this week?',
  ];

  if (!isPremium) {
    return (
      <Card className="p-6 bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-amber-100 rounded-lg">
            <Lock className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-neutral-900 mb-1">Context Journal</h3>
            <p className="text-sm text-neutral-600 mb-3">
              Add notes to your check-ins to track context, patterns, and cause-effect relationships.
            </p>
            <Button
              size="sm"
              onClick={() => { window.location.href = '/pricing'; }}
              className="gap-2"
            >
              See plans
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" />
              <h3 className="font-medium text-neutral-900">Context Journal</h3>
            </div>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
            >
              <Eye className="w-4 h-4" />
              {showHistory ? 'Hide' : 'View'} History
            </button>
          </div>

          <div className="space-y-3">
            {isPremium && (
              <div className="flex flex-wrap gap-2">
                {guidedPrompts.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setNote((n) => (n ? `${n}\n\n${p}: ` : `${p}: `))}
                    className="text-xs px-2 py-1 rounded-full border border-indigo-200 bg-white/80 text-indigo-800 hover:bg-indigo-50"
                  >
                    {p.slice(0, 42)}…
                  </button>
                ))}
              </div>
            )}
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What happened today? Any important context?"
              maxLength={maxNoteLength}
              rows={isPro ? 6 : 4}
              className="resize-none"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-neutral-500">
                {note.length}/{maxNoteLength} characters
              </span>
              {isPro && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleVoiceRecord}
                  className="gap-2"
                >
                  <Mic className={`w-4 h-4 ${isRecording ? 'text-red-500' : ''}`} />
                  {isRecording ? 'Stop Recording' : 'Voice Note'}
                </Button>
              )}
            </div>

            {voiceNote && (
              <div className="tension-glow bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mic className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-purple-900">Voice note recorded</span>
                </div>
                <button
                  onClick={() => setVoiceNote(null)}
                  className="text-purple-600 hover:text-purple-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <Button onClick={handleSave} className="w-full">
              Save Journal Entry
            </Button>
          </div>

          {/* Pattern Detection */}
          {pattern && (
            <div className="tension-glow bg-white/60 border border-indigo-200 rounded-lg p-3">
              <p className="text-xs font-medium text-indigo-900 mb-1">
                📊 Pattern Detected
              </p>
              <p className="text-sm text-indigo-800">
                You've mentioned "{pattern.phrase}" in {pattern.count} journal entries
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Journal History */}
      {showHistory && journalHistory.length > 0 && (
        <Card className="p-6">
          <h4 className="font-medium text-neutral-900 mb-4">Journal History</h4>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {journalHistory
              .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
              .map((entry) => (
                <div
                  key={entry.id}
                  className="bg-neutral-50 rounded-lg p-3 border border-neutral-200"
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs text-neutral-500">
                      {new Date(entry.timestamp).toLocaleDateString()} at{' '}
                      {new Date(entry.timestamp).toLocaleTimeString()}
                    </span>
                    {entry.voiceNote && (
                      <Mic className="w-3 h-3 text-purple-500" />
                    )}
                  </div>
                  <p className="text-sm text-neutral-700">{entry.note}</p>
                </div>
              ))}
          </div>
        </Card>
      )}
    </div>
  );
}
